import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import { communityService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getImageUrl } from '../utils/imageUtils';
import SedesEditor from '../components/sedes/SedesEditor';

const TAB_REQUESTS = 'requests';
const TAB_MEMBERS = 'members';
const TAB_BLOCKED = 'blocked';
const TAB_SEDES = 'sedes';
const TAB_SETTINGS = 'settings';

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-lg shadow-lg text-sm font-medium animate-fade-in ${
        toast.type === 'error'
          ? 'bg-error-container text-on-error-container'
          : 'bg-on-surface text-surface'
      }`}
    >
      {toast.message}
    </div>
  );
}

function Avatar({ user, size = 'md' }) {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  if (user?.profilePictureId) {
    return (
      <img
        src={getImageUrl(user.profilePictureId)}
        alt={user.name}
        className={`${sizeClasses} rounded-full object-cover shrink-0`}
      />
    );
  }
  return (
    <div className={`${sizeClasses} rounded-full bg-primary-container flex items-center justify-center shrink-0`}>
      <span className="font-bold text-on-primary-container">{user?.name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
}

function ConfirmModal({ open, title, body, onConfirm, onCancel, confirmLabel, confirmClass }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-surface-container-lowest rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
        <h3 className="text-base font-bold text-on-surface mb-2">{title}</h3>
        {body && <p className="text-sm text-on-surface-variant mb-5">{body}</p>}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={confirmClass || 'px-4 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 transition-opacity'}
          >
            {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BanReasonModal({ open, onConfirm, onCancel, userName }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-surface-container-lowest rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
        <h3 className="text-base font-bold text-on-surface mb-1">{t('Ban from community')}</h3>
        <p className="text-sm text-on-surface-variant mb-4">
          {t('Are you sure you want to ban {{name}}?', { name: userName })}
        </p>
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Reason (optional)')}</label>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-outline-variant/30 bg-transparent text-on-surface text-sm resize-none mb-4 placeholder:text-on-surface-variant/50"
          placeholder={t('Reason (optional)')}
        />
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors"
          >
            {t('Cancelar')}
          </button>
          <button
            onClick={() => { onConfirm(reason.trim() || null); setReason(''); }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 transition-opacity"
          >
            {t('Ban from community')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shell (top-level so it never remounts on parent re-render) ─────────────────

function Shell({ children, theme }) {
  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        {children}
      </main>
    </div>
  );
}

// ── ToggleRow (top-level so it never remounts on parent re-render) ─────────────

function ToggleRow({ label, description, checked, onToggle, isPending }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-surface-container-high/30">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-on-surface">{label}</p>
        {description && <p className="text-xs text-on-surface-variant mt-0.5">{description}</p>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        disabled={isPending}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
          checked ? 'bg-primary' : 'bg-outline-variant/40'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function CommunityAdminNew() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(TAB_REQUESTS);
  const [toast, setToast] = useState(null);

  // Modals
  const [kickModal, setKickModal] = useState(null); // { userId, userName }
  const [banModal, setBanModal] = useState(null);   // { userId, userName }
  const [promoteModal, setPromoteModal] = useState(null);
  const [revokeModal, setRevokeModal] = useState(null);
  const [unbanModal, setUnbanModal] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: community, isLoading: communityLoading } = useQuery({
    queryKey: ['community', id],
    queryFn: async () => {
      const res = await communityService.getCommunityById(id);
      return res.data;
    },
    staleTime: 30_000,
    refetchOnMount: true,
  });

  // Permission gate — redirect non-admins
  useEffect(() => {
    if (!communityLoading && community && user) {
      const role = community.currentUserRole;
      if (!role || role === 'MEMBER') {
        navigate(`/communities/${id}`, { replace: true });
      }
    }
  }, [community, communityLoading, user, id, navigate]);

  const isCreator = community?.currentUserRole === 'CREATOR';
  const isAdmin = community?.currentUserRole === 'CREATOR' || community?.currentUserRole === 'ADMIN';

  const { data: joinRequests = [], isLoading: reqLoading } = useQuery({
    queryKey: ['join-requests', id, 'PENDING'],
    queryFn: async () => {
      const res = await communityService.getJoinRequests(id, { status: 'PENDING' });
      return res.data || [];
    },
    enabled: isAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: admins = [], isLoading: adminsLoading } = useQuery({
    queryKey: ['community-admins', id],
    queryFn: async () => {
      const res = await communityService.getAdmins(id);
      return res.data || [];
    },
    enabled: isAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['community-members', id],
    queryFn: async () => {
      const res = await communityService.getCommunityMembers(id, { pageSize: 50 });
      const payload = res.data;
      return Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);
    },
    enabled: isAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: blockedUsers = [], isLoading: blockedLoading } = useQuery({
    queryKey: ['community-blocks', id],
    queryFn: async () => {
      const res = await communityService.getBlockedUsers(id);
      return res.data || [];
    },
    enabled: isAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // ── Mutations ──────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: (reqId) => communityService.approveJoinRequest(id, reqId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['join-requests', id] });
      queryClient.invalidateQueries({ queryKey: ['community-members', id] });
      showToast('success', t('Join request approved'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const rejectMutation = useMutation({
    mutationFn: (reqId) => communityService.rejectJoinRequest(id, reqId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['join-requests', id] });
      showToast('success', t('Join request rejected'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const kickMutation = useMutation({
    mutationFn: (userId) => communityService.removeUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-members', id] });
      queryClient.invalidateQueries({ queryKey: ['community', id] });
      setKickModal(null);
      showToast('success', t('Usuario expulsado de la comunidad'));
    },
    onError: (err) => { setKickModal(null); showToast('error', err?.response?.data?.message || t('Error')); },
  });

  const banMutation = useMutation({
    mutationFn: ({ userId, reason }) => communityService.blockUser(id, userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-members', id] });
      queryClient.invalidateQueries({ queryKey: ['community-blocks', id] });
      setBanModal(null);
      showToast('success', t('Usuario bloqueado de la comunidad'));
    },
    onError: (err) => { setBanModal(null); showToast('error', err?.response?.data?.message || t('Error')); },
  });

  const unbanMutation = useMutation({
    mutationFn: (userId) => communityService.unblockUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-blocks', id] });
      setUnbanModal(null);
      showToast('success', t('Unban'));
    },
    onError: (err) => { setUnbanModal(null); showToast('error', err?.response?.data?.message || t('Error')); },
  });

  const promoteMutation = useMutation({
    mutationFn: (userId) => communityService.promoteAdmin(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-admins', id] });
      setPromoteModal(null);
      showToast('success', t('Make Admin'));
    },
    onError: (err) => { setPromoteModal(null); showToast('error', err?.response?.data?.message || t('Error')); },
  });

  const revokeMutation = useMutation({
    mutationFn: (userId) => communityService.revokeAdmin(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-admins', id] });
      setRevokeModal(null);
      showToast('success', t('Revoke Admin'));
    },
    onError: (err) => { setRevokeModal(null); showToast('error', err?.response?.data?.message || t('Error')); },
  });

  const approvalToggleMutation = useMutation({
    mutationFn: (value) => communityService.setLinkJoinApproval(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', id] });
      showToast('success', t('Settings'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const allowDomainJoinMutation = useMutation({
    mutationFn: (value) => communityService.updateAllowDomainJoin(id, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', id] });
      showToast('success', t('Settings'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const invitePolicyMutation = useMutation({
    mutationFn: (policy) => communityService.setInvitePolicy(id, policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', id] });
      showToast('success', t('Settings'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const [domainInput, setDomainInput] = useState('');
  useEffect(() => {
    // Sync only when the community entity itself changes (by id), not on every
    // domain property change — otherwise background refetches would reset what
    // the user is currently typing.
    if (community?.domain != null) setDomainInput(community.domain);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community?.id]);

  const domainMutation = useMutation({
    mutationFn: (domain) =>
      communityService.updateCommunity(id, {
        domain,
        name: community.name,
        description: community.description,
        invitePolicy: community.invitePolicy,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', id] });
      showToast('success', t('Dominio actualizado'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  if (communityLoading) {
    return (
      <Shell theme={theme}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      </Shell>
    );
  }

  if (!community) {
    return (
      <Shell theme={theme}>
        <div className="px-4 pt-8">
          <div className="p-4 rounded-lg bg-error-container text-on-error-container text-sm">{t('Comunidad no encontrada')}</div>
        </div>
      </Shell>
    );
  }

  const adminUserIds = new Set(admins.map((a) => a.userId ?? a.id));
  const pendingCount = Array.isArray(joinRequests) ? joinRequests.length : 0;

  const tabs = [
    { key: TAB_REQUESTS, label: t('Pending Requests'), badge: pendingCount },
    { key: TAB_MEMBERS, label: t('Members') },
    { key: TAB_BLOCKED, label: t('Blocked Users') },
    { key: TAB_SEDES, label: t('Sedes') },
    { key: TAB_SETTINGS, label: t('Settings') },
  ];

  // ── Tab content ────────────────────────────────────────────────────────────

  const renderRequests = () => {
    if (reqLoading) return <div className="py-10 text-center text-on-surface-variant text-sm">{t('Cargando...')}</div>;
    if (!joinRequests.length) {
      return (
        <div className="py-12 text-center">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant/20">inbox</span>
          <p className="text-sm text-on-surface-variant mt-3">{t('No hay solicitudes pendientes')}</p>
        </div>
      );
    }
    return (
      <div className="divide-y divide-outline-variant/10">
        {joinRequests.map((req) => (
          <div key={req.id} className="flex items-center gap-3 px-5 py-4">
            <Avatar user={req.user ?? req} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-on-surface truncate">{req.user?.name ?? req.userName ?? req.name}</p>
              <p className="text-xs text-on-surface-variant truncate">{req.user?.email ?? req.email}</p>
              {req.message && <p className="text-xs text-on-surface-variant/70 italic mt-0.5 truncate">"{req.message}"</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => approveMutation.mutate(req.id)}
                disabled={approveMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[14px]">check</span>
                {t('Approve')}
              </button>
              <button
                onClick={() => rejectMutation.mutate(req.id)}
                disabled={rejectMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface text-xs font-bold hover:bg-error/5 hover:text-error hover:border-error/30 transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[14px]">close</span>
                {t('Reject')}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMembers = () => {
    if (membersLoading || adminsLoading) return <div className="py-10 text-center text-on-surface-variant text-sm">{t('Cargando...')}</div>;
    if (!members.length) {
      return <div className="py-12 text-center text-sm text-on-surface-variant">{t('No hay miembros para mostrar')}</div>;
    }
    return (
      <div className="divide-y divide-outline-variant/10">
        {members.map((member) => {
          const memberId = member.id ?? member.userId;
          const isThisAdmin = adminUserIds.has(memberId);
          const isThisCreator = memberId === community.creatorId;
          const isSelf = memberId === user?.id;
          return (
            <div key={memberId} className="flex items-center gap-3 px-5 py-3.5">
              <Avatar user={member} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p
                    className="text-sm font-semibold text-on-surface truncate cursor-pointer hover:text-primary transition-colors"
                    onClick={() => navigate(`/users/${memberId}`)}
                  >
                    {member.name}
                  </p>
                  {isThisCreator && (
                    <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[9px] font-bold uppercase tracking-widest">
                      {t('Creador')}
                    </span>
                  )}
                  {!isThisCreator && isThisAdmin && (
                    <span className="px-1.5 py-0.5 bg-secondary/10 text-secondary rounded text-[9px] font-bold uppercase tracking-widest">
                      {t('Admin')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-on-surface-variant truncate">{member.email}</p>
              </div>
              {!isSelf && !isThisCreator && (
                <div className="flex items-center gap-1.5 shrink-0">
                  {isCreator && !isThisAdmin && (
                    <button
                      onClick={() => setPromoteModal({ userId: memberId, userName: member.name })}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface text-xs font-medium hover:bg-surface-container-high/50 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[13px]">shield</span>
                      {t('Make Admin')}
                    </button>
                  )}
                  {isCreator && isThisAdmin && (
                    <button
                      onClick={() => setRevokeModal({ userId: memberId, userName: member.name })}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface text-xs font-medium hover:bg-surface-container-high/50 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[13px]">remove_moderator</span>
                      {t('Revoke Admin')}
                    </button>
                  )}
                  <button
                    onClick={() => setKickModal({ userId: memberId, userName: member.name })}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface text-xs font-medium hover:border-error/30 hover:text-error hover:bg-error/5 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[13px]">person_remove</span>
                    {t('Kick from community')}
                  </button>
                  <button
                    onClick={() => setBanModal({ userId: memberId, userName: member.name })}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-error/10 text-error text-xs font-medium hover:bg-error/20 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[13px]">block</span>
                    {t('Ban from community')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBlocked = () => {
    if (blockedLoading) return <div className="py-10 text-center text-on-surface-variant text-sm">{t('Cargando...')}</div>;
    if (!blockedUsers.length) {
      return (
        <div className="py-12 text-center">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant/20">shield</span>
          <p className="text-sm text-on-surface-variant mt-3">{t('No hay usuarios bloqueados')}</p>
        </div>
      );
    }
    return (
      <div className="divide-y divide-outline-variant/10">
        {blockedUsers.map((block) => {
          const blockedUser = block.user ?? block;
          const userId = block.userId ?? block.id;
          return (
            <div key={userId} className="flex items-center gap-3 px-5 py-3.5">
              <Avatar user={blockedUser} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-on-surface truncate">{blockedUser.name}</p>
                {block.reason && <p className="text-xs text-on-surface-variant italic truncate">{block.reason}</p>}
              </div>
              <button
                onClick={() => setUnbanModal({ userId, userName: blockedUser.name })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface text-xs font-bold hover:bg-surface-container-high/50 transition-colors"
              >
                <span className="material-symbols-outlined text-[13px]">lock_open</span>
                {t('Unban')}
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSettings = () => (
    <div className="px-5 py-6 space-y-6">
      {/* Domain field — creator only */}
      {isCreator && (
        <div className="p-4 rounded-xl bg-surface-container-high/30 space-y-2">
          <p className="text-sm font-semibold text-on-surface">{t('Dominio')}</p>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="example.com"
              className="flex-1 px-3 py-2 rounded-lg border border-outline-variant/30 bg-transparent text-on-surface text-sm placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
            />
            <button
              onClick={() => domainMutation.mutate(domainInput.trim())}
              disabled={domainMutation.isPending || !domainInput.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-on-primary text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {t('Guardar')}
            </button>
          </div>
        </div>
      )}

      {/* Allow domain auto-join */}
      {isCreator && (
        <>
          <ToggleRow
            label={t('Permitir auto-join por dominio')}
            description={
              !community.domain
                ? t('Configurá un dominio primero')
                : t('Usuarios con correo del dominio configurado se unen automáticamente')
            }
            checked={!!community.allowDomainJoin}
            onToggle={() => {
              if (!community.domain) return;
              allowDomainJoinMutation.mutate(!community.allowDomainJoin);
            }}
            isPending={allowDomainJoinMutation.isPending || !community.domain}
          />
        </>
      )}

      {/* Invite policy selector */}
      {isCreator && (
        <div className="p-4 rounded-xl bg-surface-container-high/30 space-y-2">
          <p className="text-sm font-semibold text-on-surface">{t('¿Quién puede crear links de invitación?')}</p>
          <div className="flex gap-3 mt-2">
            {[
              { value: 'ADMIN_ONLY', label: t('Solo admins') },
              { value: 'OPEN', label: t('Cualquier miembro') },
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => invitePolicyMutation.mutate(value)}
                disabled={invitePolicyMutation.isPending}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 ${
                  community.invitePolicy === value
                    ? 'bg-primary text-on-primary border-primary'
                    : 'bg-transparent text-on-surface-variant border-outline-variant/30 hover:bg-surface-container-high/50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Member-created link approval — creator only, hidden when only admins can create links */}
      {isCreator && community.invitePolicy !== 'ADMIN_ONLY' && (
        <ToggleRow
          label={t('Si un miembro crea un link de invitacion, necesita que lo acepte un admin')}
          description={t('Members joining via link will need admin approval')}
          checked={!!community.linkJoinRequiresApproval}
          onToggle={() => approvalToggleMutation.mutate(!community.linkJoinRequiresApproval)}
          isPending={approvalToggleMutation.isPending}
        />
      )}

      {/* Active join links */}
      <JoinLinksSection
        communityId={id}
        isAdmin={isAdmin}
        showToast={showToast}
      />
    </div>
  );

  return (
    <Shell theme={theme}>
      <div className="px-4 mobile-sm:px-6 tablet-sm:px-8 pt-5 tablet-sm:pt-8 pb-24 tablet-sm:pb-12 max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <header className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/communities/${id}`)}
            className="w-9 h-9 rounded-xl border border-outline-variant/30 flex items-center justify-center hover:bg-surface-container transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-black text-on-surface truncate">{community.name}</h1>
            <p className="text-xs text-on-surface-variant font-bold uppercase tracking-wider mt-0.5">{t('Admin Dashboard')}</p>
          </div>
        </header>

        {/* Tabs */}
        <nav className="flex flex-wrap gap-1 p-1 bg-surface-container rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 min-w-[140px] flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-high/50'
              }`}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center ${
                  activeTab === tab.key ? 'bg-on-primary/20 text-on-primary' : 'bg-error text-on-error'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Tab content */}
        <div className="bg-surface-container rounded-xl overflow-hidden">
          {activeTab === TAB_REQUESTS && renderRequests()}
          {activeTab === TAB_MEMBERS && renderMembers()}
          {activeTab === TAB_BLOCKED && renderBlocked()}
          {activeTab === TAB_SEDES && (
            <div className="px-5 py-6">
              <SedesEditor
                communityId={id}
                currentSedes={community?.sedes || []}
                scheduleEnabled={!!community?.scheduleEnabled}
                onToast={showToast}
              />
            </div>
          )}
          {activeTab === TAB_SETTINGS && renderSettings()}
        </div>
      </div>

      {/* Modals */}
      <ConfirmModal
        open={!!kickModal}
        title={t('Are you sure you want to remove {{name}}?', { name: kickModal?.userName })}
        confirmLabel={t('Kick from community')}
        onConfirm={() => kickMutation.mutate(kickModal.userId)}
        onCancel={() => setKickModal(null)}
      />
      <BanReasonModal
        open={!!banModal}
        userName={banModal?.userName}
        onConfirm={(reason) => banMutation.mutate({ userId: banModal.userId, reason })}
        onCancel={() => setBanModal(null)}
      />
      <ConfirmModal
        open={!!unbanModal}
        title={t('Unban') + ' ' + (unbanModal?.userName || '')}
        confirmLabel={t('Unban')}
        confirmClass="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 transition-opacity"
        onConfirm={() => unbanMutation.mutate(unbanModal.userId)}
        onCancel={() => setUnbanModal(null)}
      />
      <ConfirmModal
        open={!!promoteModal}
        title={t('Make Admin') + ': ' + (promoteModal?.userName || '')}
        confirmLabel={t('Make Admin')}
        confirmClass="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 transition-opacity"
        onConfirm={() => promoteMutation.mutate(promoteModal.userId)}
        onCancel={() => setPromoteModal(null)}
      />
      <ConfirmModal
        open={!!revokeModal}
        title={t('Revoke Admin') + ': ' + (revokeModal?.userName || '')}
        confirmLabel={t('Revoke Admin')}
        onConfirm={() => revokeMutation.mutate(revokeModal.userId)}
        onCancel={() => setRevokeModal(null)}
      />

      <Toast toast={toast} />
    </Shell>
  );
}

// ── Join Links sub-section ─────────────────────────────────────────────────────

function JoinLinksSection({ communityId, isAdmin, showToast }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['join-links', communityId],
    queryFn: async () => {
      const res = await communityService.getJoinLinks(communityId);
      return res.data || [];
    },
    enabled: isAdmin,
  });

  const deactivateMutation = useMutation({
    mutationFn: (linkId) => communityService.deactivateJoinLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['join-links', communityId] });
      showToast('success', t('Revoke link'));
    },
    onError: (err) => showToast('error', err?.response?.data?.message || t('Error')),
  });

  const [confirmDeactivateAll, setConfirmDeactivateAll] = useState(false);

  const deactivateAllMutation = useMutation({
    mutationFn: () => communityService.deactivateAllJoinLinks(communityId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['join-links', communityId] });
      const count = res?.data?.deactivated ?? 0;
      showToast('success', t('Se invalidaron {{count}} link(s)', { count }));
      setConfirmDeactivateAll(false);
    },
    onError: (err) => {
      showToast('error', err?.response?.data?.message || t('Error'));
      setConfirmDeactivateAll(false);
    },
  });

  const copyLink = async (token) => {
    const url = `${window.location.origin}/join?token=${encodeURIComponent(token)}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('success', t('¡Enlace copiado al portapapeles!'));
    } catch {
      showToast('error', t('Error'));
    }
  };

  const activeLinks = links.filter((l) => l.active !== false && l.isActive !== false);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-on-surface">{t('Active join links')}</h3>
        <div className="flex items-center gap-2">
          {activeLinks.length > 0 && (
            confirmDeactivateAll ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-on-surface-variant">{t('¿Confirmar?')}</span>
                <button
                  onClick={() => deactivateAllMutation.mutate()}
                  disabled={deactivateAllMutation.isPending}
                  className="px-3 py-1.5 rounded-lg bg-error text-on-error text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {deactivateAllMutation.isPending ? t('Cargando...') : t('Sí, invalidar')}
                </button>
                <button
                  onClick={() => setConfirmDeactivateAll(false)}
                  className="px-3 py-1.5 rounded-lg border border-outline-variant/30 text-on-surface-variant text-xs font-medium hover:bg-surface-container transition-colors"
                >
                  {t('Cancelar')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDeactivateAll(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-error/30 text-error text-xs font-bold hover:bg-error/10 transition-colors"
              >
                <span className="material-symbols-outlined text-[14px]">link_off</span>
                {t('Invalidar todos los links')}
              </button>
            )
          )}
        </div>
      </div>

      {isLoading && <div className="text-xs text-on-surface-variant">{t('Cargando...')}</div>}
      {!isLoading && !activeLinks.length && (
        <p className="text-xs text-on-surface-variant">{t('No active join links')}</p>
      )}
      <div className="space-y-2">
        {activeLinks.map((link) => (
          <div key={link.id} className="flex items-center gap-3 p-3 rounded-xl bg-surface-container-high/30">
            <span className="material-symbols-outlined text-[18px] text-primary">link</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono text-on-surface truncate">/join?token={link.token?.slice(0, 16)}…</p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  link.maxUses === 1 ? 'bg-secondary-container text-on-secondary-container' : 'bg-surface-container text-on-surface-variant'
                }`}>
                  {link.maxUses === 1 ? t('Uso único') : t('Sin restricción de usos')}
                </span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  link.requiresApproval !== false ? 'bg-tertiary-container text-on-tertiary-container' : 'bg-primary-container text-on-primary-container'
                }`}>
                  {link.requiresApproval !== false ? t('Con aprobación') : t('Sin aprobación')}
                </span>
                {link.expiresAt && (
                  <span className="text-[10px] text-on-surface-variant">
                    {t('Expires')}: {new Date(link.expiresAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => copyLink(link.token)}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-surface-container-high transition-colors"
              title={t('¡Enlace copiado al portapapeles!')}
            >
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">content_copy</span>
            </button>
            <button
              onClick={() => deactivateMutation.mutate(link.id)}
              disabled={deactivateMutation.isPending}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-error/10 hover:text-error transition-colors disabled:opacity-50"
              title={t('Revoke link')}
            >
              <span className="material-symbols-outlined text-[16px]">link_off</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CommunityAdminNew;
